import {
  Stack,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import dayjs from "dayjs";
import { debounce, isNil } from "lodash";
import { DataView } from "primereact/dataview";
import { Paginator, type PaginatorPageChangeEvent } from "primereact/paginator";
import React, { useEffect, useState } from "react";
import {
  DocumentIngestionPipelineFilter,
  type ScannedOrderResultsForLogsQuery,
  ScannedOrderSource,
  useScannedOrderResultsForLogsQuery,
  useSchemasQuery,
} from "../../../generated/graphql";
import ContactSelect from "../../common/contact-select";
import DateRangePicker, { type DateOption } from "../../date-range-picker";
import PipelineFilter from "../../doc-ingestion/pipeline-filter";
import CompanySelect, { GLOBAL_SCHEMA_NO_COMPANY } from "../company-select";
import LogDetailDialog from "./log-detail-dialog";
import LogItem from "./log-item";
import { pageCursorArgs } from "./page-cursor-args";

const pageSize = 18;

const Logs = () => {
  const [companyUuid, setCompanyUuid] = useState<string>();
  const [contactUuid, setContactUuid] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>();
  const [scannedOrderSource, setScannedOrderSource] =
    useState<ScannedOrderSource | null>(null);
  const [showFailed, setShowFailed] = useState(false);
  const [pipeline, setPipeline] = useState<DocumentIngestionPipelineFilter>(
    DocumentIngestionPipelineFilter.All,
  );
  const [dateOption, setDateOption] = useState<DateOption>({
    startDate: dayjs(new Date()).subtract(1, "days").toDate(),
    endDate: null,
  });
  const [viewingResultUuid, setViewingResultUuid] = useState<string>();
  const { data: schemasData } = useSchemasQuery({
    fetchPolicy: "cache-first",
  });
  const [page, setPage] = useState<number>(0);

  // Every request carries the same filters; paging only adds cursor arguments
  // on top. Building them once keeps a filter from being dropped on the paged
  // refetch while the header still reports the unfiltered total.
  const filters = {
    companyUuid,
    contactUuid,
    searchText,
    scannedOrderSource,
    startDate: dateOption.startDate,
    endDate: dateOption.endDate,
    failed: showFailed ? true : null,
    pipeline,
  };

  const {
    data: scannedOrderResultsData,
    loading,
    refetch,
  } = useScannedOrderResultsForLogsQuery({
    variables: {
      args: {
        first: pageSize,
        ...filters,
      },
    },
  });

  // A filter change refetches from the head of the list, so the page index has
  // to follow it back to zero.
  useEffect(() => {
    setPage(0);
  }, [
    companyUuid,
    contactUuid,
    searchText,
    scannedOrderSource,
    dateOption.startDate,
    dateOption.endDate,
    showFailed,
    pipeline,
  ]);

  const onChangeSearchText = debounce((value: string) => {
    setSearchText(value);
  }, 300);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const pageInfo =
      scannedOrderResultsData?.scannedOrderResultsForAdmin.pageInfo;
    const cursorArgs = pageCursorArgs({
      requestedPage: event.page,
      currentPage: page,
      pageCount: event.pageCount,
      pageSize,
      startCursor: pageInfo?.startCursor,
      endCursor: pageInfo?.endCursor,
    });
    // A move cursor pagination cannot express leaves the list where it is,
    // rather than moving the page label off the rows on screen.
    if (isNil(cursorArgs)) {
      return;
    }
    refetch({
      args: {
        ...cursorArgs,
        ...filters,
      },
    });
    setPage(event.page);
  };

  const header = () => {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            sx={{ minWidth: 350 }}
            size="small"
            label="Search order or email"
            onChange={(e) => {
              onChangeSearchText(e.target.value);
            }}
          />
          <CompanySelect
            allowNoCompany
            noLabel
            currentCompanyUuid={companyUuid}
            minWidth={200}
            maxWidth={300}
            onChange={(option) => {
              if (!isNil(option)) {
                setCompanyUuid(
                  option.value === GLOBAL_SCHEMA_NO_COMPANY
                    ? undefined
                    : option.value,
                );
              }
            }}
          />
          <ContactSelect
            noLabel
            companyUuid={companyUuid}
            currentContactUuid={contactUuid}
            setContactUuid={setContactUuid}
            minWidth={200}
          />
          <DateRangePicker dateOption={dateOption} onChange={setDateOption} />
          <FormControl sx={{ minWidth: "100px" }}>
            <InputLabel shrink id="source-label">
              Source
            </InputLabel>
            <Select
              notched
              size="small"
              labelId="source-label"
              value={scannedOrderSource}
              label="Source"
              onChange={(event) => {
                setScannedOrderSource(
                  event.target.value === "All"
                    ? null
                    : (event.target.value as ScannedOrderSource),
                );
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value={ScannedOrderSource.EmailIntegration}>
                Email
              </MenuItem>
              <MenuItem value={ScannedOrderSource.UploadedDocument}>
                Uploaded
              </MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox value={showFailed} />}
            label="Failed"
            onChange={(e, checked) => {
              setShowFailed(checked);
            }}
          />
          <PipelineFilter value={pipeline} onChange={setPipeline} />
        </Stack>
        <Paginator
          first={page}
          rows={1}
          // One "record" is one page: `rows={1}` makes the paginator's page
          // count the number of pages. Rounding up keeps the last partial page
          // reachable and keeps the report from printing a fraction.
          totalRecords={Math.ceil(
            (scannedOrderResultsData?.scannedOrderResultsForAdmin.totalCount ??
              0) / pageSize,
          )}
          template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          onPageChange={onPageChange}
        />
      </Stack>
    );
  };

  const listTemplate = (
    scannedOrderResults: ScannedOrderResultsForLogsQuery["scannedOrderResultsForAdmin"],
  ) => {
    if (isNil(scannedOrderResults)) return;
    return (
      <Grid container spacing={1}>
        {scannedOrderResults.edges.map(({ node: scannedOrderResult }) => (
          <LogItem
            key={scannedOrderResult.uuid}
            scannedOrderResult={scannedOrderResult}
            matchedSchemaName={
              schemasData?.schemas.find(
                (s) => s.uuid === scannedOrderResult.schemaUuid,
              )?.name
            }
            setViewingResultUuid={setViewingResultUuid}
          />
        ))}
      </Grid>
    );
  };

  return (
    <Stack sx={{ p: 2 }}>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-expect-error */}
      <DataView
        loading={loading}
        value={scannedOrderResultsData?.scannedOrderResultsForAdmin}
        listTemplate={listTemplate}
        layout="grid"
        header={header()}
      />
      {!isNil(viewingResultUuid) && (
        <LogDetailDialog
          resultUuid={viewingResultUuid}
          onClose={() => {
            setViewingResultUuid(undefined);
          }}
        />
      )}
    </Stack>
  );
};

export default Logs;
